import { z } from 'zod';

import {
  ProfileId,
  OrganizationId,
  AuthenticatorId,
  ProfileType,
  ProfileKeyGenerationId,
  Email,
} from 'backend-client-web';
import {
  AsymEncryptedBlob,
  RawEncryptionPublicKey,
  EncryptionPublicKeySignature,
  loadAsymEncryptedBlob,
  loadRawEncryptionPublicKey,
  loadEncryptionPublicKeySignature,
  loadRawSigningPublicKey,
  loadSigningPublicKeySignature,
  RawSigningPublicKey,
  SigningPublicKeySignature,
} from 'vault-crypto';

import { brandedUint8Array } from '../brands';
import { makeProtobufEnumSchema } from '../enumSchemas';
import { uint8ArraySchema } from '../uint8ArraySchema';
import { WithUnknownValuePlaceholder } from '../unknownValuePlaceholder';
import { undefinedFromNullish } from '../util/undefinedFromNullish';

const schemaProfileAuthenticatorLock = z.object({
  authenticatorId: z.uuid(),
  encryptedHighSecurityProfileSeed: uint8ArraySchema,
  encryptedStorableProfileSeed: uint8ArraySchema,
});

interface PersistableProfileAuthenticatorLock {
  authenticatorId: AuthenticatorId;
  encryptedHighSecurityProfileSeed: Uint8Array;
  encryptedStorableProfileSeed: Uint8Array;
}

const schemaProfileProfileLock = z.object({
  lockingProfileId: z.uuid(),
  lockingProfileKeyGenerationId: z.uuid(),
  encryptedHighSecurityProfileSeed: uint8ArraySchema,
  encryptedStorableProfileSeed: uint8ArraySchema,
});

interface PersistableProfileProfileLock {
  lockingProfileId: ProfileId;
  lockingProfileKeyGenerationId: ProfileKeyGenerationId;
  encryptedHighSecurityProfileSeed: Uint8Array;
  encryptedStorableProfileSeed: Uint8Array;
}

interface PersistableProfile {
  readonly id: ProfileId;
  readonly profileType: WithUnknownValuePlaceholder<ProfileType>;
  readonly keyGenerationId: ProfileKeyGenerationId;

  readonly email: Email | undefined;
  readonly unverifiedEmail: Email | undefined;

  readonly organizationId: OrganizationId | undefined;

  readonly highSecurityIdentitySigPubKey: RawSigningPublicKey;
  readonly highSecurityVaultKeyEncPubKey: RawEncryptionPublicKey;
  readonly highSecurityVaultKeyEncPubKeySignature: EncryptionPublicKeySignature;
  readonly highSecurityProfileSeedEncPubKey: RawEncryptionPublicKey;
  readonly highSecurityProfileSeedEncPubKeySignature: EncryptionPublicKeySignature;
  readonly storableSigPubKey: RawSigningPublicKey;
  readonly storableSigPubKeySignature: SigningPublicKeySignature;
  readonly storableVaultKeyEncPubKey: RawEncryptionPublicKey;
  readonly storableVaultKeyEncPubKeySignature: EncryptionPublicKeySignature;
  readonly storableProfileSeedEncPubKey: RawEncryptionPublicKey;
  readonly storableProfileSeedEncPubKeySignature: EncryptionPublicKeySignature;

  readonly authenticatorLocks: PersistableProfileAuthenticatorLock[];
  readonly profileLock: PersistableProfileProfileLock | undefined;

  // Storable seeds are now always persisted directly, since persistables are always stored encrypted
  readonly storableSeed?: Uint8Array;

  /**
   * @deprecated previously used to persist admin profiles via the logged-in
   * UnlockedAuthenticator. Kept around for fallback loading from persistable.
   * TODO: This can be removed at some point, together with turning storableSeed non-optional.
   */
  readonly localEncryptedStorableSeed?: AsymEncryptedBlob;
}

const PersistableProfile = z.object({
  id: z.uuid(),
  profileType: makeProtobufEnumSchema(ProfileType, ProfileType.UNKNOWN),
  keyGenerationId: z.uuid(),

  email: undefinedFromNullish(z.string()),
  unverifiedEmail: undefinedFromNullish(z.string()),

  organizationId: undefinedFromNullish(z.uuid()),

  highSecurityIdentitySigPubKey: brandedUint8Array(loadRawSigningPublicKey),
  highSecurityVaultKeyEncPubKey: brandedUint8Array(loadRawEncryptionPublicKey),
  highSecurityVaultKeyEncPubKeySignature: brandedUint8Array(loadEncryptionPublicKeySignature),
  highSecurityProfileSeedEncPubKey: brandedUint8Array(loadRawEncryptionPublicKey),
  highSecurityProfileSeedEncPubKeySignature: brandedUint8Array(loadEncryptionPublicKeySignature),
  storableSigPubKey: brandedUint8Array(loadRawSigningPublicKey),
  storableSigPubKeySignature: brandedUint8Array(loadSigningPublicKeySignature),
  storableVaultKeyEncPubKey: brandedUint8Array(loadRawEncryptionPublicKey),
  storableVaultKeyEncPubKeySignature: brandedUint8Array(loadEncryptionPublicKeySignature),
  storableProfileSeedEncPubKey: brandedUint8Array(loadRawEncryptionPublicKey),
  storableProfileSeedEncPubKeySignature: brandedUint8Array(loadEncryptionPublicKeySignature),

  authenticatorLocks: z.array(schemaProfileAuthenticatorLock),
  profileLock: undefinedFromNullish(schemaProfileProfileLock),

  storableSeed: uint8ArraySchema.optional(),
  localEncryptedStorableSeed: brandedUint8Array(loadAsymEncryptedBlob).optional(),
});

export { PersistableProfile };
